import { motion, useInView } from 'framer-motion'
import { useRef } from 'react'
import { fadeInUp, staggerContainer } from '@/lib/motion'

const steps = [
  {
    n: '01',
    title: 'Get an API key',
    desc: 'Sign up, create an application in your dashboard, and copy your App ID + API key. Free tier is live in seconds.',
    code: `$ curl -sX POST https://api.konexzero.com/api/v1/credentials \\
    -H "X-App-Id: $APP_ID" -H "X-Api-Key: $API_KEY"`,
  },
  {
    n: '02',
    title: 'Integrate',
    desc: 'Use our REST API to create rooms, join participants, and manage media tracks — POST /join, /publish, /subscribe. No SDK required; generate your own client from the OpenAPI spec.',
    code: `// Any HTTP client works.
await fetch(\`/api/v1/rooms/\${room}/join\`, {
  method: 'POST', headers,
  body: JSON.stringify({ participant_id, sdp })
})`,
  },
  {
    n: '03',
    title: 'Go live',
    desc: 'Deploy to production with zero configuration. Our edge network handles scaling automatically, and signed webhooks keep your backend in sync.',
    code: `# webhook delivery
event:     participant.joined
room:      standup-2026-05
signature: sha256=...
timestamp: 1714660800`,
  },
]

export function HowItWorksSection() {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: '-100px' })

  return (
    <section
      id="how-it-works"
      aria-label="How it works"
      className="relative py-28 lg:py-40"
    >
      {/* Sunken band: subtle gradient surface, not a hard border */}
      <div
        aria-hidden
        className="absolute inset-0 -z-10"
        style={{
          background:
            'linear-gradient(180deg, transparent 0%, hsl(var(--surface-sunken)) 12%, hsl(var(--surface-sunken)) 88%, transparent 100%)',
        }}
      />

      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <motion.div
          ref={ref}
          variants={staggerContainer}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          className="mb-20 max-w-2xl"
        >
          <motion.div variants={fadeInUp} className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
            03 / lifecycle
          </motion.div>
          <motion.h2
            variants={fadeInUp}
            className="mt-3 font-heading text-3xl font-bold leading-tight tracking-tight sm:text-4xl"
          >
            How It Works
          </motion.h2>
          <motion.p
            variants={fadeInUp}
            className="mt-4 text-lg text-muted-foreground"
          >
            From sign-up to production-ready realtime communication in three simple steps.
          </motion.p>
        </motion.div>

        <motion.ol
          variants={staggerContainer}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          className="space-y-12 lg:space-y-16"
        >
          {steps.map((s, i) => (
            <motion.li
              key={s.n}
              variants={fadeInUp}
              className="grid items-start gap-8 lg:grid-cols-12 lg:gap-12"
            >
              {/* Step number — display serif, big */}
              <div className="lg:col-span-3">
                <div className="flex items-baseline gap-3">
                  <span className="font-heading text-7xl leading-none signal-text">{s.n}</span>
                  <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
                    step
                  </span>
                </div>
                <h3 className="mt-3 font-heading text-2xl font-semibold tracking-tight">
                  {s.title}
                </h3>
              </div>

              {/* Description */}
              <div className="lg:col-span-4">
                <p className="text-lg leading-relaxed text-muted-foreground">
                  {s.desc}
                </p>
              </div>

              {/* Code peek */}
              <div className="lg:col-span-5">
                <pre className="surface-card overflow-x-auto p-5 font-mono text-[12.5px] leading-relaxed text-foreground/85">
                  <code>{s.code}</code>
                </pre>
              </div>

              {/* Connector — only between steps */}
              {i < steps.length - 1 && (
                <div className="hidden lg:col-span-12 lg:block" aria-hidden>
                  <div className="ml-[7%] h-12 w-px bg-gradient-to-b from-[hsl(var(--brand-2)/0.4)] to-transparent" />
                </div>
              )}
            </motion.li>
          ))}
        </motion.ol>
      </div>
    </section>
  )
}
